// src/contexts/DataContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import MockDataService from '../services/MockDataService';

// Création du contexte
const DataContext = createContext();

// Hook personnalisé pour utiliser le contexte
export const useData = () => useContext(DataContext);

// Fournisseur du contexte qui met à disposition les données et les fonctions
export const DataProvider = ({ children }) => {
  // États pour stocker les données
  const [pendingOrders, setPendingOrders] = useState([]);
  const [batchAssignments, setBatchAssignments] = useState([]);
  const [resources, setResources] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fonction pour charger toutes les données initiales
  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // En mode simulation, utiliser MockDataService
      setResources(MockDataService.getResources());
      setCycles(MockDataService.getCycles());
      setPendingOrders(MockDataService.getPendingOrders());
      setBatchAssignments(MockDataService.getBatchAssignments());
      setStats(MockDataService.getProductionStats());
      
      // Simuler un délai réseau pour un comportement plus réaliste
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      setError('Impossible de charger les données. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour assigner un OF à une fournée
  const assignOrderToBatch = async (orderId, batchNumber) => {
    try {
      // Simulation de mise à jour côté serveur
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Mise à jour locale des données
      setPendingOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId ? { ...order, assignedTo: batchNumber } : order
        )
      );
      
      // Mise à jour des compteurs de la fournée
      updateBatchCounters(batchNumber);
      
      return { success: true };
    } catch (err) {
      console.error('Erreur lors de l\'assignation de l\'OF:', err);
      return { success: false, error: err.message };
    }
  };

  // Fonction pour mettre à jour les compteurs d'une fournée
  const updateBatchCounters = (batchNumber) => {
    // Calculer le total des ressources utilisées par cette fournée
    const assignedOrders = pendingOrders.filter(order => order.assignedTo === batchNumber);
    const usedVacuumPorts = assignedOrders.reduce((sum, order) => sum + order.vacuumPorts, 0);
    const usedTc = assignedOrders.reduce((sum, order) => sum + order.tc, 0);
    
    // Mettre à jour la fournée
    setBatchAssignments(prevBatches => 
      prevBatches.map(batch => 
        batch.batchNumber === batchNumber
          ? { ...batch, usedVacuumPorts, usedTc }
          : batch
      )
    );
  };

  // Fonction pour créer une nouvelle fournée
  const createBatch = async (batchData) => {
    try {
      // Générer un identifiant unique pour la nouvelle fournée
      const date = new Date();
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const batchCount = batchAssignments.length + 1;
      const batchNumber = `B${day}${month}-${batchCount.toString().padStart(2, '0')}`;
      
      const newBatch = {
        ...batchData,
        batchNumber,
        status: 'scheduled',
        usedVacuumPorts: 0,
        usedTc: 0
      };
      
      // Simulation de création côté serveur
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Mise à jour locale
      setBatchAssignments(prev => [...prev, newBatch]);
      
      return { success: true, batch: newBatch };
    } catch (err) {
      console.error('Erreur lors de la création de la fournée:', err);
      return { success: false, error: err.message };
    }
  };

  // Fonction pour mettre à jour une fournée existante
  const updateBatch = async (batchNumber, batchData) => {
    try {
      // Simulation de mise à jour côté serveur
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Mise à jour locale
      setBatchAssignments(prevBatches => 
        prevBatches.map(batch => 
          batch.batchNumber === batchNumber
            ? { ...batch, ...batchData }
            : batch
        )
      );
      
      return { success: true };
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la fournée:', err);
      return { success: false, error: err.message };
    }
  };

  // Fonction pour obtenir les détails d'une fournée
  const getBatchDetails = (batchNumber) => {
    return MockDataService.getBatchDetails(batchNumber);
  };

  // Fonction pour mettre à jour le statut d'un OF
  const updateOrderStatus = async (orderId, status, alertMessage = null) => {
    try {
      // Simulation de mise à jour côté serveur
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Mise à jour locale
      setPendingOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...order, status, alert: alertMessage } 
            : order
        )
      );
      
      // Mise à jour des statistiques
      setStats(MockDataService.getProductionStats());
      
      return { success: true };
    } catch (err) {
      console.error('Erreur lors de la mise à jour du statut de l\'OF:', err);
      return { success: false, error: err.message };
    }
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadAllData();
  }, []);

  // Valeur du contexte à fournir
  const contextValue = {
    // Données
    pendingOrders,
    batchAssignments,
    resources,
    cycles,
    stats,
    loading,
    error,
    
    // Fonctions
    loadAllData,
    assignOrderToBatch,
    createBatch,
    updateBatch,
    updateBatchCounters,
    getBatchDetails,
    updateOrderStatus
  };

  return (
    <DataContext.Provider value={contextValue}>
      {children}
    </DataContext.Provider>
  );
};

export default DataContext;